import type { Season } from '$types';
import type { Progress } from './prefs.svelte';
import { findEpisode, nextEpisode } from './cinemeta';

export interface HistoryGroup {
  key: string;
  latest: Progress;
  /** Episode order, not the order in which a rewatch updated the resume points. */
  entries: Progress[];
  resume: Progress | null;
}

/** A view of the caller's current history only. No cache, storage, or account merging. */
export function groupHistory(items: readonly Progress[], resumable: readonly Progress[]): HistoryGroup[] {
  const groups = new Map<string, HistoryGroup>();
  const resumeKeys = new Set(resumable.map((item) => item.key));
  for (const item of [...items].sort((a, b) => b.updatedAt - a.updatedAt)) {
    if (!item.finished && !resumeKeys.has(item.key)) continue;
    const key = item.kind === 'series' ? `series:${item.imdbId}` : item.key;
    let group = groups.get(key);
    if (!group) {
      group = { key, latest: item, entries: [], resume: null };
      groups.set(key, group);
    }
    if (group.entries.some((entry) => entry.key === item.key)) continue;
    group.entries.push(item);
    if (!group.resume && resumeKeys.has(item.key)) group.resume = item;
  }
  for (const group of groups.values()) {
    group.entries.sort((a, b) => (a.season ?? 1) - (b.season ?? 1)
      || (a.episode ?? 1) - (b.episode ?? 1) || a.updatedAt - b.updatedAt);
  }
  return [...groups.values()];
}

/** Keep exact resume coordinates; only advance a completed show using known metadata. */
export function historyContinuation(group: HistoryGroup, seasons: Season[] = []): Progress | null {
  if (group.resume) return group.resume;
  const last = group.latest;
  if (last.kind !== 'series' || !last.finished || last.season === undefined || last.episode === undefined) return null;
  if (!findEpisode(seasons, last.season, last.episode)) return null;
  const next = nextEpisode(seasons, last.season, last.episode);
  if (!next) return null;
  return {
    ...last, key: `series:${last.imdbId}:${next.season}:${next.episode}`,
    season: next.season, episode: next.episode, subtitle: next.title,
    positionSec: 0, durationSec: 0, finished: false
  };
}

/**
 * THE CONTINUE ROW, ONE TILE PER SERIES (owner, 2026-09-13).
 *
 * Home and My Jovii both used to draw the raw resume points, so a weekend spent
 * on one show filled the whole row with that show and pushed everything else
 * off the end of it. The owner's rule is the one a person already holds in
 * their head: a series is ONE thing you are in the middle of, and the tile
 * resumes the episode you last watched. Films are already one thing each and
 * are untouched.
 *
 * This is `groupHistory` + `historyContinuation` — the pair My Jovii's history
 * screen has always used — so there is exactly one definition of "where am I in
 * this show" in the app, and the row and the history sheet can never disagree.
 *
 * `seasons` is deliberately optional and deliberately EMPTY at both call sites:
 * `historyContinuation` only advances a finished episode to the next one when
 * it is handed real catalogue metadata, and neither Home nor My Jovii fetches
 * per-series metadata for a row of tiles (that would be one Cinemeta request
 * per show on every page load). So a show whose last episode is finished and
 * unadvanceable simply leaves the row, which is the same answer the old
 * per-episode row gave. The sheet, which DOES fetch seasons, still offers the
 * next episode.
 *
 * Order is most-recent-first: `groupHistory` walks the history newest-first, so
 * the insertion order of the map is already the order the row wants.
 */
export interface ContinueTile {
  /** The resume point the tile draws and opens — the group's own episode. */
  item: Progress;
  /**
   * Every progress key this one tile stands for. The "x" on a series tile
   * forgets the WHOLE show, because a tile that is one thing must also be one
   * thing when you dismiss it — forgetting only the episode drawn on it would
   * leave the row redrawing the same series with the previous episode on it.
   */
  keys: string[];
}

export function continueTiles(
  items: readonly Progress[],
  resumable: readonly Progress[],
  limit = 12,
  seasons: Season[] = []
): ContinueTile[] {
  const tiles: ContinueTile[] = [];
  for (const group of groupHistory(items, resumable)) {
    const item = historyContinuation(group, seasons);
    if (!item) continue;
    tiles.push({ item, keys: group.entries.map((entry) => entry.key) });
    if (tiles.length >= limit) break;
  }
  return tiles;
}
